import axios from "axios";
import { PAYMENTS_ENDPOINTS } from "./paymentsConstants";

/**
 * Fetches the active payment configuration (threshold, currency, allowed methods).
 * @returns {Promise<Object>} Decrypted PaymentMethodConfig dict
 */
export async function fetchPaymentConfig() {
    const { data } = await axios.get(PAYMENTS_ENDPOINTS.config);
    return data;
}

/**
 * Updates the payment configuration. Only provided fields are persisted server-side.
 * @param {Object} payload - Partial config (card_threshold, currency, hold_expiration_hours, allowed_methods_low, allowed_methods_high)
 * @returns {Promise<Object>} Updated PaymentMethodConfig dict
 */
export async function updatePaymentConfig(payload) {
    const { data } = await axios.put(PAYMENTS_ENDPOINTS.config, payload);
    return data;
}

/**
 * Fetches the list of payments stuck in REFUND_MANUAL_REQUIRED.
 * Each entry is enriched with reservation_id, reservation_status, last_refund_attempt.
 * @returns {Promise<Array>}
 */
export async function fetchPendingRefunds() {
    const { data } = await axios.get(PAYMENTS_ENDPOINTS.refundsPending);
    return data;
}

/**
 * Fetches the full audit trail of refund attempts for a payment.
 * @param {number|string} paymentId - Target payment ID
 * @returns {Promise<Array>} Array of RefundAttempt dicts
 */
export async function fetchRefundHistory(paymentId) {
    const { data } = await axios.get(PAYMENTS_ENDPOINTS.refundsHistory(paymentId));
    return data;
}

/**
 * Queues an immediate refund retry for a stuck payment.
 * Each call is logged in refund_attempts and counts toward the retry budget.
 * @param {number|string} paymentId - Target payment ID
 * @returns {Promise<Object>} { status, payment_id, amount_cents, triggered_by_admin_id }
 */
export async function retryRefund(paymentId) {
    const { data } = await axios.post(PAYMENTS_ENDPOINTS.refundsRetry(paymentId));
    return data;
}

/**
 * Marks a payment as refunded outside Stripe (manual SEPA refund).
 * @param {number|string} paymentId - Target payment ID
 * @param {Object} payload - { refunded_amount?, note? }
 * @returns {Promise<Object>} { payment, reservation_status }
 */
export async function markRefunded(paymentId, payload) {
    const { data } = await axios.post(PAYMENTS_ENDPOINTS.markRefunded(paymentId), payload);
    return data;
}

/**
 * Marks a reservation as paid when the Stripe webhook was lost.
 * @param {number|string} reservationId - Target reservation ID
 * @param {Object} payload - { payment_intent_id?, payment_method_type?, note? }
 * @returns {Promise<Object>} { reservation_id, status, payment }
 */
export async function markReservationPaid(reservationId, payload) {
    const { data } = await axios.post(PAYMENTS_ENDPOINTS.markPaid(reservationId), payload);
    return data;
}

/**
 * Generates a Stripe Checkout payment link for a B2B / commercial sale.
 * @param {Object} payload - { client_email, client_name?, description?, amount, currency?, reference? }
 * @returns {Promise<Object>} CommercialTransaction dict including payment_link_url
 */
export async function sendPaymentLink(payload) {
    const { data } = await axios.post(PAYMENTS_ENDPOINTS.sendPaymentLink, payload);
    return data;
}

/**
 * Fetches the 10 most recent payments (sorted by created_at DESC server-side).
 * Used as the default browse view when no search query is active.
 * @returns {Promise<Array>} Array of enriched Payment dicts
 */
export async function fetchLastPayments() {
    const { data } = await axios.get(PAYMENTS_ENDPOINTS.last);
    return Array.isArray(data) ? data : [];
}

/**
 * Searches up to 10 payments matching the substring across address / city / email / reference.
 * @param {string} term - Search substring (case-insensitive)
 * @returns {Promise<Array>} Array of enriched Payment dicts
 */
export async function searchPayments(term) {
    const { data } = await axios.get(PAYMENTS_ENDPOINTS.search(term));
    return Array.isArray(data) ? data : [];
}
